import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { supabase } from '../lib/supabase';
import { useTranslation } from '../lib/i18n';

interface ShareDialogProps {
  documentId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface SharedUser {
  id: string;
  email: string;
  role_type: 'viewer' | 'editor';
}

const ShareDialog = ({ documentId, isOpen, onClose }: ShareDialogProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'viewer' | 'editor'>('viewer');
  const [sharedUsers, setSharedUsers] = useState<SharedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [userLookupStatus, setUserLookupStatus] = useState<'idle' | 'searching' | 'not-found' | 'found'>('idle');
  const [validatingEmail, setValidatingEmail] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSharedUsers();
      setShareLink(`${window.location.origin}/editor/${documentId}`);
    }
  }, [isOpen, documentId]);

  const loadSharedUsers = async () => {
    try {
      setLoading(true);
      const { data: shares, error: sharesError } = await supabase
        .from('document_shares')
        .select(`
          id,
          shared_with,
          role_type,
          users (
            id,
            email
          )
        `)
        .eq('document_id', documentId);

      if (sharesError) throw sharesError;

      const formattedShares = shares?.map(share => ({
        id: share.users.id,
        email: share.users.email,
        role_type: share.role_type,
      })) || [];

      setSharedUsers(formattedShares);
      setError('');
    } catch (error: any) {
      console.error('Error loading shared users:', error);
      setError(t('share.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.toLowerCase());
  };

  const lookupUser = async (email: string): Promise<{ id: string; email: string } | null> => {
    if (!validateEmail(email)) {
      setUserLookupStatus('idle');
      return null;
    }

    try {
      setUserLookupStatus('searching');
      setValidatingEmail(true);

      const normalizedEmail = email.toLowerCase().trim();

      // Check if user is already shared
      const isAlreadyShared = sharedUsers.some(
        user => user.email.toLowerCase() === normalizedEmail
      );

      if (isAlreadyShared) {
        setError(t('share.alreadyAccess'));
        setUserLookupStatus('not-found');
        return null;
      }

      // Look up user by email via SECURITY DEFINER function (bypasses users RLS)
      const { data: rpcResult, error } = await supabase
        .rpc('lookup_user_by_email', { lookup_email: normalizedEmail });

      if (error) throw error;

      if (rpcResult && rpcResult.length > 0) {
        setUserLookupStatus('found');
        return rpcResult[0];
      }

      setUserLookupStatus('not-found');
      return null;
    } catch (error: any) {
      console.error('Error looking up user:', error);
      setUserLookupStatus('not-found');
      return null;
    } finally {
      setValidatingEmail(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setError('');
    setUserLookupStatus('idle');

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (newEmail && validateEmail(newEmail)) {
      const timeout = setTimeout(() => {
        lookupUser(newEmail);
      }, 500);
      setSearchTimeout(timeout);
    }
  };

  const handleShare = async () => {
    if (!email) {
      setError(t('share.enterEmail'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('share.invalidEmail'));
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const user = await lookupUser(email);

      if (!user) {
        throw new Error(t('share.userNotFoundMsg'));
      }

      // Create new share
      const { error: shareError } = await supabase
        .from('document_shares')
        .insert({
          document_id: documentId,
          shared_with: user.id,
          role_type: role,
        });

      if (shareError) {
        if (shareError.code === '23505') { // Unique violation
          throw new Error(t('share.alreadyAccess'));
        }
        throw shareError;
      }

      setEmail('');
      setUserLookupStatus('idle');
      await loadSharedUsers();
      setError('');
    } catch (error: any) {
      console.error('Error sharing document:', error);
      setError(error.message || t('share.failedShare'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveShare = async (userId: string) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('document_shares')
        .delete()
        .eq('document_id', documentId)
        .eq('shared_with', userId);

      if (error) throw error;

      setSharedUsers(prev => prev.filter(user => user.id !== userId));
      setError('');
    } catch (error: any) {
      console.error('Error removing share:', error);
      setError(t('share.failedRemove'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: 'viewer' | 'editor') => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('document_shares')
        .update({ role_type: newRole })
        .eq('document_id', documentId)
        .eq('shared_with', userId);

      if (error) throw error;

      setSharedUsers(prev =>
        prev.map(user =>
          user.id === userId ? { ...user, role_type: newRole } : user
        )
      );
      setError('');
    } catch (error: any) {
      console.error('Error updating role:', error);
      setError(t('share.failedRole'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      setError(t('share.failedCopy'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 animate-fadeIn">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{t('share.title')}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">{t('share.link')}</label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600"
              />
              <Button onClick={copyToClipboard} variant="outline" size="sm">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">{t('share.withEmail')}</label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="flex-1 relative">
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder={t('share.emailPlaceholder')}
                    className={`w-full p-2 rounded border ${
                      userLookupStatus === 'found' 
                        ? 'border-green-500 dark:border-green-600' 
                        : userLookupStatus === 'not-found'
                        ? 'border-red-500 dark:border-red-600'
                        : 'border-gray-300 dark:border-gray-600'
                    } dark:bg-gray-700`}
                    disabled={loading}
                  />
                  {(userLookupStatus === 'searching' || validatingEmail) && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    </div>
                  )}
                </div>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'viewer' | 'editor')}
                  className="p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  disabled={loading}
                >
                  <option value="viewer">{t('share.viewer')}</option>
                  <option value="editor">{t('share.editor')}</option>
                </select>
                <Button 
                  onClick={handleShare} 
                  disabled={loading || userLookupStatus !== 'found'}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('share.sharing')}
                    </>
                  ) : (
                    t('share.share')
                  )}
                </Button>
              </div>
              {userLookupStatus === 'not-found' && email && (
                <p className="text-sm text-red-500 dark:text-red-400">
                  {t('share.userNotFound')}
                </p>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-4">{t('share.sharedWith')}</h3>
            <div className="space-y-3">
              {loading && sharedUsers.length === 0 ? (
                <div className="text-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-blue-500" />
                  <p className="text-gray-500 dark:text-gray-400">{t('share.loadingUsers')}</p>
                </div>
              ) : sharedUsers.length > 0 ? (
                sharedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{user.email}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                        {user.role_type}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <select
                        value={user.role_type}
                        onChange={(e) => handleUpdateRole(user.id, e.target.value as 'viewer' | 'editor')}
                        className="p-1 text-sm rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-600"
                        disabled={loading}
                      >
                        <option value="viewer">{t('share.viewer')}</option>
                        <option value="editor">{t('share.editor')}</option>
                      </select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveShare(user.id)}
                        disabled={loading}
                        className="text-red-500 hover:text-red-700"
                      >
                        {t('share.remove')}
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  {t('share.noUsers')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareDialog;